import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, FiltersBar, Icon, Page, SearchInput, SingleSelectFilter } from '~/components';
import { useDocumentTitle, useSearchParams, useSessionStorage } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import NavBox from './components/NavBox';
import NavSection from './components/NavSection';
import { useReports } from './ReportsContext';

const AccordionButton = styled(Button)`
  font-size: 0.75rem;
  color: ${colors.grey55};
  opacity: 0.5;

  &:hover {
    color: ${colors.grey55};
    opacity: 1;
  }
`;

const NoResults = styled.div`
  color: ${colors.grey40};
  font-size: 0.875rem;
  margin-top: 1.5rem;
`;

export default function ReportsLibraryPage() {
  useDocumentTitle('Reports Library');

  const sessionStorage = useSessionStorage({ key: 'reports_library__sections' });

  const [sections, setSections] = useState(() => {
    const sections = {
      pinned: true,
      time: true,
      expenses: true,
      utilization: true,
      forecast: true,
      plan: true,
      variance: true,
      accounting: true,
      performance: true,
      pipeline: true,
      workspace: true,
    };
    try {
      if (sessionStorage.hasValue()) {
        const value = JSON.parse(sessionStorage.get());
        if (value) {
          sections.pinned = !!value.pinned;
          sections.time = !!value.time;
          sections.expenses = !!value.expenses;
          sections.utilization = !!value.utilization;
          sections.forecast = !!value.forecast;
          sections.plan = !!value.plan;
          sections.variance = !!value.variance;
          sections.accounting = !!value.accounting;
          sections.performance = !!value.performance;
          sections.pipeline = !!value.pipeline;
          sections.workspace = !!value.workspace;
        }
      }
    } finally {
      sessionStorage.set(JSON.stringify(sections));
    }
    return sections;
  });

  const all = Object.keys(sections).every((section) => sections[section]);

  const toggleSection = (section) => {
    const value = { ...sections, [section]: !sections[section] };
    setSections(value);
    sessionStorage.set(JSON.stringify(value));
  };

  const toggleAllSections = () => {
    const value = {
      pinned: !all,
      time: !all,
      expenses: !all,
      utilization: !all,
      forecast: !all,
      plan: !all,
      variance: !all,
      accounting: !all,
      performance: !all,
      pipeline: !all,
      workspace: !all,
    };

    setSections(value);
    sessionStorage.set(JSON.stringify(value));
  };

  const [params, setParams] = useState(() => ({ q: '', scopeKey: null }));
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        scopeKey: { valid: ['system', 'workspace'] },
      }),
      [],
    ),
    sessionKey: 'reports_library__params',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });
  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      if (params) {
        setParams((state) => ({ ...state, ...params }));
        setSearchParamsStatus('ready');
      }
    });
  }, [searchParams, searchParamsStatus]);

  const reportsContext = useReports();

  const config = useMemo(() => {
    if (!reportsContext?.reports || searchParamsStatus !== 'ready') return null;

    const allReports = reportsContext.reports?.filter((r) => ['system', 'workspace'].includes(r.scopeKey));

    const filter = (reports, section) => {
      let results = _(reports)
        .filter((r) => (section ? r.section === section : true))
        .orderBy(['name'], ['asc'])
        .value();

      if (params.q) {
        const fuse = new Fuse(results, {
          keys: ['name', 'description', 'section'],
          threshold: 0.1,
          ignoreLocation: true,
        });

        results = fuse.search(params.q).map(({ item }) => item);
      }

      if (params.scopeKey) {
        results = results.filter((r) => r.scopeKey === params.scopeKey);
      }

      return results;
    };

    const sections = [
      { id: 'pinned', title: 'Featured', reports: filter(allReports.filter((r) => r.isPinned)) },
      { id: 'time', title: 'Time', reports: filter(allReports, 'time') },
      { id: 'expenses', title: 'Expenses', reports: filter(allReports, 'expenses') },
      { id: 'utilization', title: 'Utilization', reports: filter(allReports, 'utilization') },
      { id: 'forecast', title: 'Forecast', reports: filter(allReports, 'forecast') },
      { id: 'plan', title: 'Plan', reports: filter(allReports, 'plan') },
      { id: 'variance', title: 'Variance', reports: filter(allReports, 'variance') },
      { id: 'accounting', title: 'Accounting', reports: filter(allReports, 'accounting') },
      { id: 'performance', title: 'Performance', reports: filter(allReports, 'performance') },
      { id: 'pipeline', title: 'Pipeline', reports: filter(allReports, 'pipeline') },
      { id: 'workspace', title: 'Workspace', reports: filter(allReports, 'workspace') },
    ];

    return sections.filter(({ reports }) => reports.length);
  }, [reportsContext, params.q, searchParamsStatus, params.scopeKey]);

  const handleFilter = ({ target: { name, value } }) => {
    setParams((params) => ({ ...params, [name]: value }));
    searchParams.set({ [name]: value });
  };

  return (
    <Page>
      <Page.Header>
        <Page.Info>
          <Page.Eyebrow>Reports</Page.Eyebrow>
          <Page.Title>
            Library
            <AccordionButton style={{ marginLeft: '0.5rem' }} isAnchor onClick={() => toggleAllSections()}>
              <Icon icon={all ? 'angle-double-down' : 'angle-double-up'} />
            </AccordionButton>
          </Page.Title>
        </Page.Info>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <div style={{ gridColumn: '1 / 3' }}>
            <SearchInput name="q" value={params.q} onChange={handleFilter} placeholder="Search" autoFocus />
          </div>

          <SingleSelectFilter
            name="scopeKey"
            placeholder="Report Type"
            onChange={handleFilter}
            value={params.scopeKey}
            options={[
              { id: 'system', name: 'Standard Report' },
              { id: 'workspace', name: 'Custom Report' },
            ]}
          />
        </FiltersBar>
      </Page.Filters>

      {!config ? (
        <PageLoader />
      ) : (
        <>
          {config.map((section) => (
            <NavSection
              key={section.id}
              title={section.title}
              expanded={sections[section.id]}
              onToggle={() => toggleSection(section.id)}>
              {_.map(section.reports, (report) => (
                <NavBox key={report.id} area="library" report={report} />
              ))}
            </NavSection>
          ))}

          {!config.length && <NoResults>No results found.</NoResults>}
        </>
      )}
    </Page>
  );
}
